import React, { Component } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { io } from 'socket.io-client';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import API_BASE_URL from '../config/apiConfig';
import ChatSkeleton from '../components/ui/ChatSkeleton';
import '../styles/ChatPage.css';

const SOCKET_URL = API_BASE_URL.replace('/api', '');

// withRouter HOC for Class Components in React Router v6
function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        let location = useLocation();
        let navigate = useNavigate();
        let params = useParams();
        return (
            <Component
                {...props}
                router={{ location, navigate, params }}
                // Compatibility with older withRouter pattern if needed
                location={location}
                navigate={navigate}
                params={params}
            />
        );
    }
    return ComponentWithRouterProp;
}

class ChatPage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            partners: [],
            selectedPartner: null,
            messages: [],
            newMessage: '',
            user: JSON.parse(sessionStorage.getItem('user')),
            token: sessionStorage.getItem('token'),
            socket: null,
            isUploading: false,
            typingUser: null,
            isEditing: false,
            activeDropdownId: null,
            filePreview: null,
            isLoading: true,
            isHistoryLoading: false,
            isScrollPositioning: false,
            searchQuery: ''
        };
        this.containerRef = React.createRef();
        this.messagesEndRef = React.createRef();
        this.fileInputRef = React.createRef();
        this.typingTimeout = null;
    }

    componentDidMount() {
        if (!this.state.token) {
            this.props.navigate('/login');
            return;
        }
        this.initSocket();
        this.fetchPartners().then(() => {
            this.handleInitialPartner();
        });
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.selectedPartner?._id !== this.state.selectedPartner?._id) {
            if (this.state.selectedPartner) {
                const roomId = this.getRoomId(this.state.user, this.state.selectedPartner);
                sessionStorage.setItem('activeChatRoomId', roomId);
                this.joinRoom(roomId);
                this.fetchHistory(roomId);
                this.markAsRead(roomId);
                // Reset editing state when switching partners
                this.setState({ isEditing: false, editingMessageId: null, newMessage: '', activeDropdownId: null });
            } else {
                sessionStorage.removeItem('activeChatRoomId');
            }
        }

        // If we just finished loading history, scroll instantly to bottom
        if (prevState.isHistoryLoading && !this.state.isHistoryLoading) {
            this.scrollToBottom('auto');
        } else if (prevState.messages.length < this.state.messages.length) {
            // New message arrived/sent - use smooth scroll
            this.scrollToBottom('smooth');
        } else if (prevState.selectedPartner?._id !== this.state.selectedPartner?._id) {
            // Partner switched - ensure we are at bottom (even if empty)
            this.scrollToBottom('auto');
        }
    }

    componentWillUnmount() {
        sessionStorage.removeItem('activeChatRoomId');
        if (this.state.socket) {
            this.state.socket.close();
        }
        if (this.typingTimeout) clearTimeout(this.typingTimeout);
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event) => {
        if (this.state.activeDropdownId && !event.target.closest('.message-actions-dropdown') && !event.target.closest('.whatsapp-dropdown-container')) {
            this.setState({ activeDropdownId: null });
        }
    }

    initSocket = () => {
        const socket = io(SOCKET_URL, {
            path: '/api/socket.io',
            transports: ["websocket"]
        });

        socket.on('connect', () => {
            socket.emit('identify', this.state.token);
        });

        socket.on('receiveMessage', (message) => {
            const currentUserId = this.state.user?.id || this.state.user?._id;
            const roomId = this.getRoomId(this.state.user, this.state.selectedPartner);
            if (message.roomId === roomId) {
                this.setState(prevState => {
                    const senderId = typeof message.senderId === 'object' ? (message.senderId.id || message.senderId._id) : message.senderId;
                    const isMe = String(senderId) === String(currentUserId);

                    if (isMe) {
                        // Check if we have an optimistic message to replace
                        const optimisticIndex = prevState.messages.findLastIndex(m => m.isOptimistic && m.message === message.message);
                        if (optimisticIndex !== -1) {
                            const newMessages = [...prevState.messages];
                            newMessages[optimisticIndex] = message;
                            return { messages: newMessages };
                        }
                    }

                    // Avoid duplicates if already added
                    if (prevState.messages.some(m => String(m._id || m.id) === String(message._id || message.id))) {
                        return null;
                    }

                    return {
                        messages: [...prevState.messages, message]
                    };
                });

                // Mark as read if received while chat is open
                const senderId = typeof message.senderId === 'object' ? (message.senderId.id || message.senderId._id) : message.senderId;
                if (currentUserId && String(senderId) !== String(currentUserId)) {
                    this.markAsRead(roomId);
                }
            }
            // Move partner to top of the list
            this.movePartnerToTop(message.roomId);
        });

        socket.on('messagesRead', (data) => {
            const currentRoomId = this.getRoomId(this.state.user, this.state.selectedPartner);
            if (data.roomId === currentRoomId) {
                this.setState(prevState => ({
                    messages: prevState.messages.map(msg => {
                        const senderIdStr = typeof msg.senderId === 'string' ? msg.senderId : msg.senderId?._id;
                        if (senderIdStr === this.state.user?._id) {
                            return { ...msg, isRead: true };
                        }
                        return msg;
                    })
                }));
            }
        });

        socket.on('userTyping', ({ roomId, userName }) => {
            const currentRoomId = this.getRoomId(this.state.user, this.state.selectedPartner);
            if (roomId === currentRoomId) {
                this.setState({ typingUser: userName });
            }
        });

        socket.on('userStoppedTyping', ({ roomId }) => {
            const currentRoomId = this.getRoomId(this.state.user, this.state.selectedPartner);
            if (roomId === currentRoomId) {
                this.setState({ typingUser: null });
            }
        });

        socket.on('unreadUpdate', (data) => {
            // Defensive: ensure we don't count messages sent by ourselves
            const currentUserId = this.state.user?.id || this.state.user?._id;
            if (data && String(data.senderId) === String(currentUserId)) return;

            this.setState(prevState => {
                const newPartners = [...prevState.partners];
                const index = newPartners.findIndex(p => this.getRoomId(this.state.user, p) === data.roomId);

                if (index !== -1) {
                    const [partner] = newPartners.splice(index, 1);
                    const partnerId = String(partner._id || partner.id);
                    const isCurrentChat = prevState.selectedPartner && String(prevState.selectedPartner._id || prevState.selectedPartner.id) === partnerId;

                    // Use the server-provided count if available, otherwise fallback to local increment
                    const newCount = typeof data.partnerUnreadCount !== 'undefined'
                        ? data.partnerUnreadCount
                        : (isCurrentChat ? 0 : (partner.unreadCount || 0) + 1);

                    const updatedPartner = {
                        ...partner,
                        unreadCount: isCurrentChat ? 0 : newCount
                    };

                    newPartners.unshift(updatedPartner);
                } else {
                    // Partner not in local list (e.g., new chat), so we re-fetch the list
                    this.fetchPartners();
                }

                return { partners: newPartners };
            });
        });

        socket.on('unreadCleared', () => {
            // Re-fetch partners to get synchronized state
            this.fetchPartners();
        });

        socket.on('messageDeleted', ({ messageId, deleteType }) => {
            if (deleteType === 'everyone') {
                this.setState(prevState => ({
                    messages: prevState.messages.filter(m => (String(m._id || m.id)) !== String(messageId))
                }));
            }
        });

        socket.on('messageEdited', ({ messageId, newMessage }) => {
            this.setState(prevState => ({
                messages: prevState.messages.map(m => {
                    if (String(m._id || m.id) === String(messageId)) {
                        return { ...m, message: newMessage, isEdited: true };
                    }
                    return m;
                })
            }));
        });

        this.setState({ socket });
    }

    movePartnerToTop = (roomId) => {
        this.setState(prevState => {
            const index = prevState.partners.findIndex(p => this.getRoomId(this.state.user, p) === roomId);
            if (index > 0) { // If > 0, we need to move it (0 means already at top)
                const newPartners = [...prevState.partners];
                const [partner] = newPartners.splice(index, 1);
                newPartners.unshift(partner);
                return { partners: newPartners };
            }
            return null; // No state change if already at top or not found
        });
    }

    getRoomId = (me, partner) => {
        if (!me || !partner) return '';
        const myId = String(me._id || me.id || '');
        const partnerId = String(partner._id || partner.id || '');
        if (!myId || !partnerId) return '';

        // 1. Super Admin Case (Compatibility with superadmin_companyadmin_ format)
        if (me.role === 'SUPER_ADMIN' || partner.role === 'SUPER_ADMIN') {
            if (me.role === 'SUPER_ADMIN' && partner.role === 'SUPER_ADMIN') {
                const ids = [myId, partnerId].sort();
                return `superadmin_self_${ids[0]}_${ids[1]}`;
            }
            const otherId = me.role === 'SUPER_ADMIN' ? partnerId : myId;
            return `superadmin_companyadmin_${otherId}`;
        }

        // 2. Shreeja Support (null companyId) Case
        const isMeSupport = !me.companyId && !me.company_id;
        const isPartnerSupport = !partner.companyId && !partner.company_id;

        if (isMeSupport || isPartnerSupport) {
            const myCompanyId = me.companyId || me.company_id;
            const partnerCompanyId = partner.companyId?.id || partner.companyId || partner.company_id?.id || partner.company_id;
            const companyId = myCompanyId || partnerCompanyId;

            const supportId = isMeSupport ? myId : partnerId;
            const companyPersonId = isMeSupport ? partnerId : myId;
            // Use _user_ prefixes to ensure socket authorization works for everyone
            return `company_${companyId}_support_staff_user_${supportId}_with_user_${companyPersonId}`;
        }

        // 3. Regular Company Logic (Commutative)
        if (me.role === 'USER' && partner.role === 'USER') {
            const ids = [myId, partnerId].sort();
            const companyId = me.companyId || me.company_id;
            return `company_${companyId}_user_${ids[0]}_user_${ids[1]}`;
        }

        // Admin + User (Legacy format)
        const userId = me.role === 'USER' ? myId : partnerId;
        const companyId = me.companyId || me.company_id || (partner.companyId?.id || partner.companyId || partner.company_id?.id || partner.company_id);
        return `company_${companyId}_admin_user_${userId}`;
    }

    handleInitialPartner = () => {
        const { location } = this.props;
        if (location?.state?.partner) {
            const partner = location.state.partner;
            this.setState(prevState => {
                const partnerExists = prevState.partners.find(p => p._id === partner._id);
                // Merge data if exists to ensure we have userProfile and companyName
                const finalPartner = partnerExists ? { ...partnerExists, ...partner } : partner;
                return {
                    selectedPartner: finalPartner,
                    partners: partnerExists ? prevState.partners : [partner, ...prevState.partners]
                };
            });
            window.history.replaceState(null, '');
        }
    }

    joinRoom = (roomId) => {
        if (this.state.socket) {
            this.state.socket.emit('joinRoom', {
                token: this.state.token,
                roomId
            });
        }
    }

    fetchPartners = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/chat/partners`, {
                headers: { 'Authorization': `Bearer ${this.state.token}` }
            });
            this.setState({ partners: res.data });
        } catch (error) {
            console.error('Error fetching partners:', error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    fetchHistory = async (roomId) => {
        this.setState({ isHistoryLoading: true, messages: [] });
        try {
            const res = await axios.get(`${API_BASE_URL}/chat/history/${roomId}`, {
                headers: { 'Authorization': `Bearer ${this.state.token}` }
            });
            this.setState({ messages: res.data, isHistoryLoading: false }, () => {
                // Scroll instantly once DOM is updated
                this.scrollToBottom('auto');
                // Double-check scroll after a brief layout tick in case of images
                setTimeout(() => this.scrollToBottom('auto'), 50);
            });
        } catch (error) {
            console.error('Error fetching history:', error);
            this.setState({ isHistoryLoading: false });
        }
    }

    markAsRead = async (roomId) => {
        try {
            const partnerId = this.state.selectedPartner?._id || this.state.selectedPartner?.id;
            await axios.put(`${API_BASE_URL}/chat/read/${roomId}${partnerId ? `?partnerId=${partnerId}` : ''}`, {}, {
                headers: { 'Authorization': `Bearer ${this.state.token}` }
            });
            this.setState(prevState => ({
                partners: prevState.partners.map(p => {
                    if (this.getRoomId(this.state.user, p) === roomId) {
                        return { ...p, unreadCount: 0 };
                    }
                    return p;
                })
            }));
            this.state.socket?.emit('markRead', { token: this.state.token, roomId });
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    }

    handleSendMessage = (e) => {
        if (e) e.preventDefault();
        const { newMessage, selectedPartner, socket, user, token, isEditing, editingMessageId } = this.state;
        if (!newMessage.trim() || !selectedPartner || !socket) return;

        const roomId = this.getRoomId(user, selectedPartner);

        if (isEditing) {
            this.performEdit(editingMessageId, newMessage);
        } else {
            // Optimistic update
            const tempId = 'temp_' + Date.now();
            const optimisticMsg = {
                _id: tempId,
                id: tempId,
                senderId: {
                    _id: user.id || user._id,
                    userName: user.userName,
                    role: user.role,
                    userProfile: user.user_profile
                },
                receiverId: selectedPartner._id,
                message: newMessage,
                roomId: roomId,
                createdAt: new Date().toISOString(),
                isRead: false,
                isOptimistic: true
            };

            this.setState(prevState => ({
                messages: [...prevState.messages, optimisticMsg],
                newMessage: '',
                isEditing: false,
                editingMessageId: null
            }), () => {
                this.scrollToBottom('smooth');
            });

            socket.emit('sendMessage', {
                token,
                roomId,
                message: newMessage,
                receiverId: selectedPartner._id,
                receiverRole: this.getPartnerRoleEnum(selectedPartner.role)
            });
        }
        if (user) {
            socket.emit('stopTyping', { roomId, userName: user.userName });
        }
    }

    performEdit = async (messageId, newMessage) => {
        const { token, socket, user, selectedPartner } = this.state;
        try {
            await axios.put(`${API_BASE_URL}/chat/edit/${messageId}`, { newMessage }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            this.setState(prevState => ({
                messages: prevState.messages.map(m => {
                    if ((m._id || m.id) === messageId) {
                        return { ...m, message: newMessage, isEdited: true };
                    }
                    return m;
                })
            }));

            const roomId = this.getRoomId(user, selectedPartner);
            socket?.emit('editMessage', { token, roomId, messageId, newMessage });

        } catch (error) {
            console.error('Edit failed:', error);
            Swal.fire('Error', error.response?.data?.message || 'Failed to edit message', 'error');
        }
    }

    getPartnerRoleEnum = (role) => {
        if (role === 'SUPER_ADMIN') return 'superAdmin';
        if (role === 'ADMIN') return 'companyAdmin';
        return 'user';
    }

    handleTyping = (e) => {
        this.setState({ newMessage: e.target.value });
        const { socket, user, selectedPartner } = this.state;
        if (!socket || !selectedPartner) return;

        const roomId = this.getRoomId(user, selectedPartner);
        socket.emit('typing', { roomId, userName: user.userName });

        if (this.typingTimeout) clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            if (this.state.socket && this.state.user && this.state.selectedPartner) {
                this.state.socket.emit('stopTyping', { roomId, userName: user.userName });
            }
        }, 2000);
    }

    handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file || !this.state.selectedPartner) return;

        if (file.size > 50 * 1024 * 1024) {
            Swal.fire('Error', 'File size exceeds 50MB limit.', 'error');
            return;
        }

        const url = URL.createObjectURL(file);
        let type = 'document';
        if (file.type.startsWith('image/')) type = 'image';
        else if (file.type.startsWith('video/')) type = 'video';

        this.setState({
            filePreview: { file, url, type, caption: '' }
        });

        if (this.fileInputRef.current) this.fileInputRef.current.value = '';
    }

    handleCancelFilePreview = () => {
        if (this.state.filePreview?.url) {
            URL.revokeObjectURL(this.state.filePreview.url);
        }
        this.setState({ filePreview: null });
    }

    handleSendFilePreview = async () => {
        const { filePreview, selectedPartner, token, socket, user } = this.state;
        if (!filePreview || !selectedPartner) return;

        this.setState({ isUploading: true });
        const formData = new FormData();
        formData.append('file', filePreview.file);

        try {
            const res = await axios.post(`${API_BASE_URL}/chat/upload`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const { fileUrl, fileType, fileName, fileSize } = res.data;
            const roomId = this.getRoomId(user, selectedPartner);

            // Optimistic update for file
            const tempId = 'temp_file_' + Date.now();
            const optimisticMsg = {
                _id: tempId,
                id: tempId,
                senderId: {
                    _id: user.id || user._id,
                    userName: user.userName,
                    role: user.role,
                    userProfile: user.user_profile
                },
                receiverId: selectedPartner._id,
                message: filePreview.caption,
                fileUrl,
                fileType,
                fileName,
                fileSize,
                roomId: roomId,
                createdAt: new Date().toISOString(),
                isRead: false,
                isOptimistic: true
            };

            this.setState(prevState => ({
                messages: [...prevState.messages, optimisticMsg]
            }), () => {
                this.scrollToBottom('smooth');
            });

            socket.emit('sendMessage', {
                token: token,
                roomId,
                message: filePreview.caption,
                receiverId: selectedPartner._id,
                receiverRole: this.getPartnerRoleEnum(selectedPartner.role),
                fileUrl,
                fileType,
                fileName,
                fileSize
            });
            this.handleCancelFilePreview();
        } catch (error) {
            console.error('File upload failed:', error);
            const errorMsg = error.response?.data?.message || 'Unknown error';
            Swal.fire('Error', 'File upload failed: ' + errorMsg, 'error');
        } finally {
            this.setState({ isUploading: false });
        }
    }

    scrollToBottom = (behavior = "smooth") => {
        const container = this.containerRef.current;
        if (container) {
            if (behavior === 'auto') {
                // Temporarily disable any CSS scroll-behavior smooth so this is truly instant
                container.style.scrollBehavior = 'auto';
                container.scrollTop = container.scrollHeight;

                // Keep it at bottom if there are slight layout shifts
                container.scrollTo({ top: container.scrollHeight, behavior: 'instant' });

                // Restore original behavior after next paint
                requestAnimationFrame(() => {
                    container.style.scrollBehavior = '';
                });
            } else {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: behavior
                });
            }
        }
    }

    getFileUrl = (path) => {
        if (!path || path === 'undefined' || path === 'null') return '';
        if (path.startsWith('http') || path.startsWith('data:')) return path;

        // Normalize slashes
        let normalizedPath = path.replace(/\\/g, '/');

        // Ensure it has the 'uploads/' prefix for the backend to serve it correctly
        if (normalizedPath.includes('uploads/')) {
            // Extract everything from 'uploads/' onwards
            normalizedPath = 'uploads/' + normalizedPath.split('uploads/')[1];
        } else {
            // Prepend 'uploads/' if it's a relative path without it
            normalizedPath = 'uploads/' + (normalizedPath.startsWith('/') ? normalizedPath.substring(1) : normalizedPath);
        }

        // Final URL construction using API_BASE_URL so that reverse proxies serve uploads correctly via /api/uploads/
        const separator = normalizedPath.startsWith('/') ? '' : '/';
        return encodeURI(`${API_BASE_URL}${separator}${normalizedPath}`);
    }

    renderFileContent = (msg, isMe) => {
        if (!msg.fileUrl) return null;
        const fullUrl = this.getFileUrl(msg.fileUrl);

        switch (msg.fileType) {
            case 'image':
                return (
                    <div className="chat-file-preview">
                        <img src={fullUrl} alt={msg.fileName} className="img-thumbnail chat-img" onClick={() => window.open(fullUrl, '_blank')} />
                    </div>
                );
            case 'video':
                return (
                    <div className="chat-file-preview">
                        <video controls className="chat-video">
                            <source src={fullUrl} />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                );
            case 'document':
            case 'other':
                return (
                    <div className="chat-file-preview">
                        <a
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`btn btn-sm mt-1 ${isMe ? 'border rounded' : 'btn-outline-primary'}`}
                            style={isMe ? { color: 'white', borderColor: 'rgba(255,255,255,0.6)', backgroundColor: 'rgba(255,255,255,0.1)' } : {}}
                            download={msg.fileName}
                        >
                            <i className="fa fa-download me-1"></i> {msg.fileName || 'Download File'}
                        </a>
                    </div>
                );
            default:
                return null;
        }
    }

    handleBackToList = () => {
        this.setState({ selectedPartner: null });
    }

    renderFilePreviewModal = () => {
        const { filePreview, isUploading } = this.state;
        if (!filePreview) return null;

        return (
            <div className="file-preview-modal-overlay">
                <div className="file-preview-header px-4 py-3 d-flex align-items-center">
                    <button className="btn btn-link text-white text-decoration-none me-3 p-0" onClick={this.handleCancelFilePreview} disabled={isUploading}>
                        <i className="fa fa-times fa-2x"></i>
                    </button>
                    <div className="text-white flex-grow-1 text-center truncate">
                        <div className="fw-bold">{filePreview.file.name}</div>
                        <small className="opacity-75">{(filePreview.file.size / 1024 / 1024).toFixed(2)} MB</small>
                    </div>
                </div>

                <div className="file-preview-content flex-grow-1 d-flex align-items-center justify-content-center p-4 overflow-hidden">
                    {filePreview.type === 'image' && <img src={filePreview.url} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                    {filePreview.type === 'video' && <video src={filePreview.url} controls style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                    {filePreview.type === 'document' && (
                        <div className="d-flex flex-column align-items-center text-white">
                            <i className="fa fa-file-alt fa-5x mb-3 text-muted"></i>
                            <h4>{filePreview.file.name}</h4>
                            <p className="text-muted">Document Preview not available</p>
                        </div>
                    )}
                </div>

                <div className="file-preview-footer p-4 d-flex justify-content-center align-items-center position-relative">
                    <div className="file-preview-input-wrapper position-relative w-100" style={{ maxWidth: '800px' }}>
                        <div className="d-flex align-items-center bg-white rounded-pill px-3 py-2">
                            <input
                                type="text"
                                className="form-control border-0 shadow-none bg-transparent"
                                placeholder="Type a message..."
                                value={filePreview.caption}
                                onChange={(e) => this.setState({ filePreview: { ...filePreview, caption: e.target.value } })}
                                disabled={isUploading}
                                onKeyPress={(e) => e.key === 'Enter' && !isUploading && this.handleSendFilePreview()}
                            />
                        </div>
                        <button
                            className="btn btn-success rounded-circle file-preview-send-btn position-absolute"
                            style={{ right: '-60px', bottom: '5px', width: '48px', height: '48px', backgroundColor: '#00a884', borderColor: '#00a884' }}
                            onClick={this.handleSendFilePreview}
                            disabled={isUploading}
                        >
                            {isUploading ? <span className="spinner-border spinner-border-sm text-white"></span> : <i className="fa fa-paper-plane text-white"></i>}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    formatDateSeparator = (date) => {
        const messageDate = new Date(date);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        if (messageDate.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (messageDate.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return messageDate.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        }
    }

    toggleDropdown = (msgId) => {
        this.setState(prevState => ({
            activeDropdownId: prevState.activeDropdownId === msgId ? null : msgId
        }));
    }

    handleEditClick = (msg) => {
        const now = new Date();
        const sentAt = new Date(msg.createdAt);
        const diffInMinutes = (now - sentAt) / (1000 * 60);

        if (diffInMinutes > 5) {
            Swal.fire({
                title: 'Cannot Edit',
                text: 'This message is not edited because it was sent more than 5 minutes ago.',
                icon: 'error',
                confirmButtonColor: '#3085d6',
            });
            this.setState({ activeDropdownId: null });
            return;
        }
        this.setState({ isEditing: true, editingMessageId: msg._id, newMessage: msg.message, activeDropdownId: null });
    }

    handleDeleteMessageClick = (msg) => {
        this.setState({ activeDropdownId: null });
        const { user } = this.state;
        const senderIdStr = (msg.senderId && typeof msg.senderId === 'object') ? msg.senderId._id : msg.senderId;
        const isMe = user && senderIdStr === user._id;

        const now = new Date();
        const sentAt = new Date(msg.createdAt);
        const diffInMinutes = (now - sentAt) / (1000 * 60);

        if (isMe && diffInMinutes <= 5) {
            Swal.fire({
                title: 'Delete Message?',
                text: 'Choose deletion method',
                icon: 'warning',
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: 'Delete for Everyone',
                denyButtonText: 'Delete for Me',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#d33',
                denyButtonColor: '#3085d6',
            }).then(async (result) => {
                if (result.isConfirmed) {
                    this.performDelete(msg._id, 'everyone');
                } else if (result.isDenied) {
                    this.performDelete(msg._id, 'me');
                }
            });
        } else {
            Swal.fire({
                title: 'Delete Message?',
                text: isMe ? 'Delete message for yourself?' : 'Delete message for yourself?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Delete for Me',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#3085d6',
            }).then(async (result) => {
                if (result.isConfirmed) {
                    this.performDelete(msg._id, 'me');
                }
            });
        }
    }

    performDelete = async (messageId, deleteType) => {
        const { token, socket, user, selectedPartner } = this.state;
        try {
            await axios.delete(`${API_BASE_URL}/chat/delete/${messageId}?deleteType=${deleteType}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            this.setState(prevState => ({
                messages: prevState.messages.filter(m => (m._id || m.id) !== messageId)
            }));

            const roomId = this.getRoomId(user, selectedPartner);
            socket?.emit('deleteMessage', { token, roomId, messageId, deleteType });

        } catch (error) {
            console.error('Delete failed:', error);
            Swal.fire('Error', 'Failed to delete message', 'error');
        }
    }

    render() {
        const { partners, selectedPartner, messages, newMessage, user, isUploading, typingUser, isEditing, activeDropdownId, isLoading, isHistoryLoading, searchQuery } = this.state;

        if (isLoading) {
            return <ChatSkeleton />;
        }

        let lastDate = null;

        const filteredPartners = partners.filter(p => {
            if (!searchQuery) return true;
            const searchStr = searchQuery.toLowerCase();
            const partnerName = p.role === 'SUPER_ADMIN' ? 'Technical Support' : (p.userName || '');
            const companyName = p.companyName || p.companyId?.companyName || '';
            return partnerName.toLowerCase().includes(searchStr) || companyName.toLowerCase().includes(searchStr);
        });

        return (
            <div className={`container-fluid p-0 chat-page-wrapper ${selectedPartner ? 'chat-selected' : ''}`}>
                <div className="row g-0 h-100">
                    <div className="col-md-3 border-end bg-light chat-sidebar-column">
                        <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center flex-shrink-0">
                            <h5 className="mb-0 fw-bold text-primary">Messages</h5>
                            <button className="btn btn-sm btn-light" type="button" onClick={this.fetchPartners}><i className="fa fa-sync"></i></button>
                        </div>
                        <div className="p-2 bg-white border-bottom flex-shrink-0">
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-light border-end-0 text-muted"><i className="fa fa-search"></i></span>
                                <input 
                                    type="text" 
                                    className="form-control bg-light border-start-0" 
                                    placeholder="Search name..." 
                                    value={searchQuery}
                                    onChange={(e) => this.setState({ searchQuery: e.target.value })}
                                    style={{ boxShadow: 'none' }}
                                />
                            </div>
                        </div>
                        <div className="list-group list-group-flush chat-partners-list overflow-auto flex-grow-1">
                            {filteredPartners.length > 0 ? filteredPartners.map(partner => (
                                <button
                                    key={partner._id}
                                    type="button"
                                    className={`list-group-item list-group-item-action border-0 mb-1 py-3 px-3 position-relative rounded-3 ${selectedPartner?._id === partner._id ? 'active bg-light text-dark border-start border-primary border-4 shadow-sm' : ''}`}
                                    style={{ transition: 'all 0.2s' }}
                                    onClick={() => this.setState({ selectedPartner: partner })}
                                >
                                    <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0">
                                            <div className={`avatar-placeholder rounded-circle ${selectedPartner?._id === partner._id ? 'bg-primary' : 'bg-secondary'} text-white d-flex align-items-center justify-content-center fw-bold shadow-sm overflow-hidden`} style={{ width: '48px', height: '48px' }}>
                                                {partner.userProfile && this.getFileUrl(partner.userProfile) ? (
                                                    <img src={this.getFileUrl(partner.userProfile)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    partner.role === 'SUPER_ADMIN' ? 'S' : (partner.userName ? partner.userName.substring(0, 1).toUpperCase() : '?')
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-grow-1 ms-3 overflow-hidden">
                                            <div className="d-flex justify-content-between">
                                                <h6 className={`mb-0 text-truncate ${selectedPartner?._id === partner._id ? 'fw-bold text-primary' : 'fw-semibold'}`}>
                                                    {partner.role === 'SUPER_ADMIN' ? 'Technical Support' : partner.userName}
                                                    {partner.role === 'SUPER_ADMIN' ? '' : (!partner.companyId ? ' (Support)' : (partner.companyName ? ` (${partner.companyName})` : ''))}
                                                </h6>
                                                {partner.unreadCount > 0 && <span className="badge rounded-pill bg-danger shadow-sm">{partner.unreadCount}</span>}
                                            </div>
                                            <small className="text-muted d-block text-truncate mt-1" style={{ fontSize: '11px' }}>
                                                {partner.role === 'ADMIN' ? (partner.companyId?.companyName || 'Company Admin') : (partner.role === 'SUPER_ADMIN' ? '' : 'User')}
                                            </small>
                                        </div>
                                    </div>
                                </button>
                            )) : (
                                <div className="p-4 text-center text-muted">No contacts found</div>
                            )}
                        </div>
                    </div>

                    <div className="col-md-9 d-flex flex-column chat-main-column bg-white position-relative">
                        {selectedPartner ? (
                            <>
                                <div className="p-3 border-bottom d-flex align-items-center justify-content-between bg-white flex-shrink-0 chat-header-fixed">
                                    <div className="d-flex align-items-center">
                                        <button
                                            className="btn btn-link text-dark me-2 d-md-none p-0"
                                            onClick={this.handleBackToList}
                                        >
                                            <i className="fa fa-arrow-left"></i>
                                        </button>
                                        <div className="avatar-placeholder rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold overflow-hidden" style={{ width: '40px', height: '40px' }}>
                                            {selectedPartner.userProfile && this.getFileUrl(selectedPartner.userProfile) ? (
                                                <img src={this.getFileUrl(selectedPartner.userProfile)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                selectedPartner.role === 'SUPER_ADMIN' ? 'S' : selectedPartner.userName.substring(0, 1).toUpperCase()
                                            )}
                                        </div>
                                        <div className="ms-3">
                                            <h6 className="mb-0 fw-bold">
                                                {selectedPartner.role === 'SUPER_ADMIN' ? 'Technical Support' : selectedPartner.userName}
                                                {selectedPartner.role === 'SUPER_ADMIN' ? '' : (!selectedPartner.companyId ? ' (Support)' : (selectedPartner.companyName ? ` (${selectedPartner.companyName})` : ''))}
                                            </h6>
                                            <small className="text-muted">
                                                {selectedPartner.role === 'SUPER_ADMIN' ? '' : (!selectedPartner.companyId ? 'Platform Support' : (selectedPartner.role === 'ADMIN' ? (selectedPartner.companyId?.companyName || 'Company Admin') : 'User'))}
                                            </small>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-grow-1 d-flex flex-column overflow-hidden">
                                    <div
                                        ref={this.containerRef}
                                        className="flex-grow-1 p-4 overflow-auto bg-light chat-messages-container d-flex flex-column"
                                    >
                                        {isHistoryLoading ? (
                                            <div className="flex-grow-1">
                                                {[...Array(6)].map((_, i) => (
                                                    <div key={i} className={`d-flex mb-4 ${i % 2 === 0 ? 'justify-content-start' : 'justify-content-end'}`}>
                                                        <div style={{ width: '45%' }}>
                                                            <Skeleton height={50} borderRadius={15} baseColor="#e5e7eb" highlightColor="#f9fafb" />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : messages.length > 0 ? (
                                            messages.map((msg, idx) => {
                                                const senderIdStr = (msg.senderId && typeof msg.senderId === 'object') ? msg.senderId._id : msg.senderId;
                                                const isMe = user && senderIdStr === user._id;
                                                const msgDate = new Date(msg.createdAt).toDateString();
                                                const showDateSeparator = msgDate !== lastDate;
                                                lastDate = msgDate;
                                                const msgId = msg._id || msg.id;

                                                return (
                                                    <React.Fragment key={idx}>
                                                        {showDateSeparator && (
                                                            <div className="d-flex justify-content-center my-4">
                                                                <div className="chat-date-separator">
                                                                    {this.formatDateSeparator(msg.createdAt)}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className={`d-flex mb-4 ${isMe ? 'justify-content-end' : 'justify-content-start'}`}>
                                                            {!isMe && (
                                                                <div className="avatar-sm rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-2 mt-auto shadow-sm overflow-hidden" style={{ width: '32px', height: '32px', fontSize: '11px' }}>
                                                                    {msg.senderId?.userProfile && this.getFileUrl(msg.senderId.userProfile) ? (
                                                                        <img src={this.getFileUrl(msg.senderId.userProfile)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                    ) : (
                                                                        msg.senderId?.role === 'SUPER_ADMIN' ? 'S' : (msg.senderId?.userName?.substring(0, 1).toUpperCase() || '?')
                                                                    )}
                                                                </div>
                                                            )}
                                                            <div className={`message-container-inner ${isMe ? 'd-flex flex-column align-items-end' : 'd-flex flex-column'}`} style={{ maxWidth: '80%', position: 'relative' }}>
                                                                <div className={`message-bubble-whatsapp p-2 px-3 ${isMe ? 'bg-primary-whatsapp' : 'bg-white-whatsapp'}`}>
                                                                    <div className="message-actions-dropdown" onClick={() => this.toggleDropdown(msgId)}>
                                                                        <i className="fa fa-chevron-down"></i>
                                                                    </div>

                                                                    {activeDropdownId === msgId && (
                                                                        <div className="whatsapp-dropdown-container">
                                                                            <div className="dropdown-menu-whatsapp">
                                                                                {isMe && !msg.fileUrl && (
                                                                                    <button className="dropdown-item-whatsapp" onClick={() => this.handleEditClick(msg)}>
                                                                                        <i className="fa fa-edit"></i> Edit
                                                                                    </button>
                                                                                )}
                                                                                <button className="dropdown-item-whatsapp text-danger" onClick={() => this.handleDeleteMessageClick(msg)}>
                                                                                    <i className="fa fa-trash"></i> Delete
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {!isMe && <div className="fw-bold mb-1" style={{ fontSize: '11px', color: '#6366f1' }}>{msg.senderId?.role === 'SUPER_ADMIN' ? 'Technical Support' : msg.senderId?.userName}</div>}
                                                                    <div className="message-content-wrapper d-flex align-items-end flex-wrap">
                                                                        <div className="message-content flex-grow-1 me-2">
                                                                            {msg.message && <div className="message-text" style={{ wordBreak: 'break-word' }}>{msg.message}</div>}
                                                                            {this.renderFileContent(msg, isMe)}
                                                                        </div>
                                                                        <div className="message-metadata d-flex align-items-center ms-auto" style={{ fontSize: '10px', opacity: 0.8, alignSelf: 'flex-end', paddingBottom: '2px' }}>
                                                                            {msg.isEdited && <span className="me-1 italic" style={{ fontSize: '9px' }}>(edited)</span>}
                                                                            <span className="me-1" style={{ whiteSpace: 'nowrap' }}>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }).toLowerCase()}</span>
                                                                            {isMe && (
                                                                                <span className="whatsapp-status-ticks">
                                                                                    {msg.isRead ? (
                                                                                        <i className="fa fa-check-double" style={{ color: '#34b7f1' }}></i>
                                                                                    ) : msg.isDelivered ? (
                                                                                        <i className="fa fa-check-double text-muted"></i>
                                                                                    ) : (
                                                                                        <i className="fa fa-check text-muted"></i>
                                                                                    )}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {isMe && (
                                                                <div className="avatar-sm rounded-circle bg-primary text-white d-flex align-items-center justify-content-center ms-2 mt-auto shadow-sm overflow-hidden" style={{ width: '32px', height: '32px', fontSize: '11px', background: '#4c1d95' }}>
                                                                    {user?.userProfile && this.getFileUrl(user.userProfile) ? (
                                                                        <img src={this.getFileUrl(user.userProfile)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                    ) : (
                                                                        user?.userName?.substring(0, 1).toUpperCase() || 'U'
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })
                                        ) : (
                                            <div className="flex-grow-1 d-flex align-items-center justify-content-center text-muted">
                                                No messages yet. Say hello!
                                            </div>
                                        )}
                                        {typingUser && (
                                            <div className="d-flex mb-3 justify-content-start">
                                                <div className="p-2 rounded-4 bg-white shadow-sm italic text-muted small">
                                                    <span className="dot-typing"></span> {typingUser} is typing...
                                                </div>
                                            </div>
                                        )}
                                        <div ref={this.messagesEndRef} />
                                    </div>

                                    <div className="p-3 border-top bg-white flex-shrink-0">
                                        {isEditing && (
                                            <div className="d-flex justify-content-between align-items-center px-3 py-1 mb-2 bg-light rounded-3 border-start border-primary border-4">
                                                <small className="text-primary fw-bold">Editing message...</small>
                                                <button className="btn btn-sm text-danger" onClick={() => this.setState({ isEditing: false, editingMessageId: null, newMessage: '' })}>
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </div>
                                        )}
                                        <form onSubmit={this.handleSendMessage} className="d-flex align-items-center">
                                            <button
                                                type="button"
                                                className="btn btn-light rounded-circle me-2"
                                                onClick={() => this.fileInputRef.current?.click()}
                                                disabled={isUploading || isEditing}
                                            >
                                                {isUploading ? <span className="spinner-border spinner-border-sm"></span> : <i className="fa fa-paperclip"></i>}
                                            </button>
                                            <input
                                                type="file"
                                                className="d-none"
                                                ref={this.fileInputRef}
                                                onChange={this.handleFileUpload}
                                            />
                                            <input
                                                type="text"
                                                className="form-control rounded-pill border-light bg-light py-2 px-3"
                                                placeholder={isEditing ? "Edit your message..." : "Type your message..."}
                                                value={newMessage}
                                                onChange={this.handleTyping}
                                            />
                                            <button type="submit" className="btn btn-primary rounded-circle ms-2" disabled={!newMessage.trim() && !isUploading}>
                                                <i className={`fa ${isEditing ? 'fa-check' : 'fa-paper-plane'}`}></i>
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                                <div className="mb-3">
                                    <i className="fa fa-comments fa-4x opacity-25"></i>
                                </div>
                                <h4 className="fw-bold">Your Messages</h4>
                                <p>Select a contact from the list to start a conversation.</p>
                            </div>
                        )}
                        {this.renderFilePreviewModal()}
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouter(ChatPage);
